import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SermonCard } from "@/components/SermonCard";
import NextShowBanner from "@/components/NextShowBanner";
import { Search, Calendar, Clock } from "lucide-react";

export default function Sermons() {
  const [items, setItems] = useState([]);
  const [series, setSeries] = useState([]);
  const [selSeries, setSelSeries] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (filters = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.series && filters.series !== "all") params.series = filters.series;
      if (filters.q) params.q = filters.q;
      const { data } = await api.get("/sermons", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [s] = await Promise.all([api.get("/sermons/series")]);
      setSeries(s.data);
      await load();
    })();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load({ series: selSeries, q });
  };

  return (
    <div data-testid="sermons-page" className="pt-32 pb-20">
      <div className="ccn-container">
        <div className="ccn-overline mb-4">The Library</div>
        <h1 className="ccn-h1 mb-4 max-w-3xl">Inspiration for Life Series.</h1>
        <p className="text-[#575A58] max-w-2xl text-lg">
          Years of messages — searchable by series, speaker or theme. Press play, take notes, walk it out.
        </p>

        <div data-testid="series-schedule" className="mt-10">
          <NextShowBanner />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
          <div
            data-testid="schedule-monday"
            className="flex items-start gap-4 px-6 py-5 rounded-2xl border border-[#C8664D]/25"
            style={{ backgroundColor: 'rgba(200, 102, 77, 0.08)' }}
          >
            <span className="w-10 h-10 flex-shrink-0 rounded-full bg-[#C8664D] text-white flex items-center justify-center">
              <Calendar className="w-4 h-4" />
            </span>
            <div>
              <div className="ccn-overline text-[#C8664D]">Inspiration for Life</div>
              <div className="font-heading text-xl text-[#1E1E1E] leading-tight mt-0.5">Mondays</div>
              <div className="text-sm text-[#575A58] mt-1">7 PM CST / 8 PM EST</div>
            </div>
          </div>

          <div
            data-testid="schedule-friday"
            className="flex items-start gap-4 px-6 py-5 rounded-2xl border border-[#DDA754]/30"
            style={{ backgroundColor: 'rgba(221, 167, 84, 0.12)' }}
          >
            <span className="w-10 h-10 flex-shrink-0 rounded-full bg-[#DDA754] text-[#2A2E2C] flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </span>
            <div>
              <div className="ccn-overline text-[#A8533E]">Friday Night Highlights</div>
              <div className="font-heading text-xl text-[#1E1E1E] leading-tight mt-0.5">Fridays</div>
              <div className="text-sm text-[#575A58] mt-1">9 PM CST / 10 PM EST</div>
            </div>
          </div>
          </div>
        </div>

        <form onSubmit={onSearch} className="mt-12 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#575A58]" />
            <input
              data-testid="sermons-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, speaker, or topic…"
              className="w-full pl-14 pr-5 py-4 rounded-full bg-white border border-[#2A2E2C]/10 focus:outline-none focus:border-[#C8664D]"
            />
          </div>
          <select
            data-testid="sermons-series-filter"
            value={selSeries}
            onChange={(e) => { setSelSeries(e.target.value); load({ series: e.target.value, q }); }}
            className="px-6 py-4 rounded-full bg-white border border-[#2A2E2C]/10 focus:outline-none focus:border-[#C8664D] min-w-[200px]"
          >
            <option value="all">All series</option>
            {series.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="submit" data-testid="sermons-search-btn" className="ccn-btn-primary">Search</button>
        </form>

        <div className="mt-12">
          {loading ? (
            <div className="text-center text-[#575A58]" data-testid="sermons-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-[#575A58] py-20" data-testid="sermons-empty">No sermons found.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((s) => <SermonCard key={s.id} sermon={s} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
